import { Injectable } from '@angular/core';
import { IFormData } from './models/formData';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ILeasingSearchResult } from './models/leasingSearchResult';
import { IMakeModels } from './models/makeModels';
import { environment } from './../../environments/environment';
import { LeasingCriteria } from './models/leasingCriteria';
import { LeasingCarCriteria } from './models/leasingCarCriteria';
import { IIdNameItem } from './models/IdNameItem';

@Injectable()
export class LeasingService {

    serviceBaseUrl = environment.extwsBaseUrl;

    constructor(private httpClient: HttpClient) { }

    getDefaultFormData(): Observable<IFormData> {
        return this.httpClient.get<IFormData>(this.serviceBaseUrl + 'officeleasings/privateleasing/formData');
    }

    searchLeasingCustomers(leasingCriteria: LeasingCriteria, carCriteriaIndex: number = null): Observable<ILeasingSearchResult> {
        const searchCriteria = {
            leasingType: 'PRIVATE',
            location: [],
            monthlyFee: null,
            contractLength: null,
            contractMileage: null,
            makes: [],
            models: [],
            bodyTypes: [],
            fuelTypes: [],
            services: [],
            acceptUsedCars: false
        };

        if (leasingCriteria.location) {
            searchCriteria.location = [leasingCriteria.location.id];
        }

        if (leasingCriteria.monthlyFee) {
            searchCriteria.monthlyFee = leasingCriteria.monthlyFee;
        }

        if (leasingCriteria.singleCarModel && carCriteriaIndex == null) {
            // if single municipality-make-model mode it is enough to explicitly set car model
            // as the single municipality is auto-selected and the make has no side effects when model is set
            searchCriteria.models = [leasingCriteria.singleCarModel];
        }

        if (carCriteriaIndex != null) {
            const carSpec: LeasingCarCriteria = leasingCriteria.carSpecs[carCriteriaIndex];
            if (carSpec.contractLength != null) {
                searchCriteria.contractLength = carSpec.contractLength;
            }
            if (carSpec.contractMileage != null) {
                searchCriteria.contractMileage = carSpec.contractMileage;
            }
            if (carSpec.make != null) {
                searchCriteria.makes = [carSpec.make.id];
            }
            if (carSpec.model != null) {
                searchCriteria.models = [carSpec.model.id];
            }
            if (carSpec.bodyType != null) {
                searchCriteria.bodyTypes = [carSpec.bodyType.id];
            }
            if (carSpec.fuelType != null) {
                searchCriteria.fuelTypes = [carSpec.fuelType.id];
            }
            if (carSpec.services != null) {
                searchCriteria.services = carSpec.services.map((service: IIdNameItem) => {
                    return service.id;
                });
            }
        }

        //console.log('Searching customers by: ' + JSON.stringify(searchCriteria));
        return this.httpClient.post<ILeasingSearchResult>(this.serviceBaseUrl + 'officeleasings/search', searchCriteria);
    }

    submitLeasingCustomers(leasingCriteria: LeasingCriteria): Observable<boolean> {

        const contactMethods: string[] = [];
        if (leasingCriteria.contactPerson.contactMethodEmail) {
            contactMethods.push('EMAIL');
        }
        if (leasingCriteria.contactPerson.contactMethodPhone) {
            contactMethods.push('MOBILE_PHONE');
        }

        const searchFormBean = {
            municipalityId: leasingCriteria.location.id,
            ownCarInformation: {
                ownCarRegistration: '',
                ownCarMileage: null,
                ownCarAdditionalInformation: ''
            },
            contactPerson: {
                name: leasingCriteria.contactPerson.name,
                emailAddress: leasingCriteria.contactPerson.emailAddress,
                phoneNumber: leasingCriteria.contactPerson.phoneNumber,
                preferedWayOfContact: contactMethods,
                preferredPhoneTime: leasingCriteria.contactPerson.prefferedPhoneTime
            },
            searchCriteria: []
        };

        if (leasingCriteria.contactPerson.hasOwnCar) {
            searchFormBean.ownCarInformation = {
                ownCarRegistration: leasingCriteria.contactPerson.ownCarRegistrationNumber,
                ownCarMileage: leasingCriteria.contactPerson.ownCarMileage,
                ownCarAdditionalInformation: leasingCriteria.contactPerson.ownCarAdditionalInformation,
            };
        }

        leasingCriteria.carSpecs.forEach((carSpec: LeasingCarCriteria) => {
            // if at least one field set
            if (carSpec.make) {
                searchFormBean.searchCriteria.push({
                    leasingType: 'PRIVATE',
                    location: [leasingCriteria.location.id],
                    monthlyFee: leasingCriteria.monthlyFee,
                    contractLength: carSpec.contractLength,
                    contractMileage: carSpec.contractMileage,
                    makes: [carSpec.make.id],
                    models: [carSpec.model.id],
                    bodyTypes: [carSpec.bodyType.id],
                    fuelTypes: [carSpec.fuelType.id],
                    services: carSpec.services.map(service => service.id),
                    additionalInformation: carSpec.additionalInformation
                });
            }
        });

        //console.log('Submitting form: ' + JSON.stringify(searchFormBean));
        return this.httpClient.post<boolean>(this.serviceBaseUrl + 'officeleasings/privateleasing/submit', searchFormBean);
    }

    getCarModels(makeId: number): Observable<IMakeModels> {
        //console.log('Getting car models for make: ' + makeId);
        return this.httpClient.get<IMakeModels>(this.serviceBaseUrl + 'carmodels/' + makeId);
    }

}
